import { useMemo } from "react";
import {
  UserCog, Building2, Users as UsersIcon, Zap, ShieldCheck, Plug,
} from "lucide-react";

/**
 * Vertical tab sidebar for the Settings page (collapses into a dropdown on
 * mobile). Keeps settings grouped by audience/intent instead of one long
 * scroll.
 */
export const SETTINGS_SECTIONS = [
  { id: "profile",      label: "Profile",       icon: UserCog,     description: "Your account, appearance & how emails are sent" },
  { id: "workspace",    label: "Workspace",     icon: Building2,   description: "Company info, branding & custom categories",         ownerAdmin: true },
  { id: "client-portal",label: "Client Portal", icon: UsersIcon,   description: "What your clients see when they upload",             ownerAdmin: true, businessOnly: true },
  { id: "automations",  label: "Automations",   icon: Zap,         description: "Scheduled reports, reminders & A/R chases",          ownerAdmin: true },
  { id: "compliance",   label: "Compliance",    icon: ShieldCheck, description: "Period locks & audit guardrails",                    ownerAdmin: true },
  { id: "integrations", label: "Integrations",  icon: Plug,        description: "Push data to QuickBooks, Stripe, and more",          ownerAdmin: true },
];

export function useVisibleSettingsSections(user, tenant) {
  return useMemo(() => {
    const isOwnerAdmin = ["owner", "admin"].includes(user?.role);
    const isBusiness = ["pro", "business"].includes(tenant?.plan);
    return SETTINGS_SECTIONS.filter((s) => {
      if (s.ownerAdmin && !isOwnerAdmin) return false;
      if (s.businessOnly && !isBusiness) return false;
      return true;
    });
  }, [user?.role, tenant?.plan]);
}

export default function SettingsTabs({ sections, activeId, onChange }) {
  return (
    <>
      {/* Desktop: vertical rail */}
      <aside className="hidden lg:block w-56 shrink-0" data-testid="settings-tabs-desktop">
        <nav className="sticky top-6 space-y-0.5">
          {sections.map((s) => {
            const Icon = s.icon;
            const active = s.id === activeId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onChange(s.id)}
                data-testid={`settings-tab-${s.id}`}
                className="group w-full flex items-start gap-3 px-3 py-2.5 text-left border-l-2 transition-colors"
                style={{
                  borderColor: active ? "var(--ll-ink)" : "transparent",
                  background: active ? "var(--ll-mint)" : "transparent",
                  color: "var(--ll-ink)",
                }}
              >
                <Icon size={15} className="mt-0.5 shrink-0 opacity-70 group-hover:opacity-100" />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm leading-tight ${active ? "font-semibold" : "font-medium"}`}>{s.label}</div>
                  <div className="text-[11px] text-[var(--ll-ink-soft)] mt-0.5 leading-snug">{s.description}</div>
                </div>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Mobile: dropdown */}
      <div className="lg:hidden mb-4" data-testid="settings-tabs-mobile">
        <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Section</label>
        <select
          value={activeId}
          onChange={(e) => onChange(e.target.value)}
          data-testid="settings-tabs-mobile-select"
          className="w-full px-3 py-2 border bg-white text-sm"
          style={{ borderColor: "var(--ll-border)" }}
        >
          {sections.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>
    </>
  );
}
